import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  useTheme,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  Autocomplete,
  Divider,
  Paper,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PhoneIcon from '@mui/icons-material/Phone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DatePicker from '../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { createCustomer, searchGuestByPhone, getHotelConfiguration } from '../services/api';
import { THEME_COLORS } from '../shared/constants/theme';
import countryCodesData from '../data/country-codes.json';
import countriesData from '../data/countries-states-cities-data';
import { countryFilterOptions, stateFilterOptions, cityFilterOptions } from '../utils/locationSearch';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getBookingDrawerPanelSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerAuditButtonSx,
  getBookingDrawerInputBg,
  getBookingDrawerDividerColor,
  getBookingDrawerTextFieldSx,
  getGuestDrawerUploadPaperSx,
  getGuestDrawerFieldBorderColor,
  createBookingDrawerOverlayProps,
  BOOKING_DRAWER_ACCENT,
} from '../utils/bookingDrawerLayout';
import { getDrawerRootSx } from '../utils/drawerOverlayProps';
import { captureNativeCameraDataUrl, getNativeCameraErrorMessage } from '../utils/nativeCameraCapture';
import {
  formatPhoneLengthLabel,
  normalizePhoneInputForCountry,
  validatePhoneForCountry,
} from '../utils/phoneValidation';
import { validateGuestDocumentImages } from '../utils/documentIdFormatters';

const ACCENT = THEME_COLORS.PRIMARY; // #4ce1b6 - match theme (same as CheckIn CUSTOM_BORDER_COLOR)

// Title options - same as CheckIn drawer add guest
const titleOptions = [
  { value: '', label: 'None' },
  { value: 'Mr', label: 'Mr' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Dr', label: 'Dr' },
  { value: 'Prof', label: 'Prof' },
  { value: 'Sir', label: 'Sir' },
  { value: 'Madam', label: 'Madam' },
];
const GENDERS = [
  { value: '', label: '—' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];
// Document type options - same as CheckIn drawer add guest
const documentUploadOptions = [
  { value: 'aadhaar', label: 'Aadhaar Card' },
  { value: 'passport', label: 'Passport' },
  { value: 'voter_id', label: 'Voter ID (EPIC)' },
  { value: 'driving_license', label: 'Driving Licence' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'government_id', label: 'Government Issued ID' },
  { value: 'oci', label: 'OCI Card' },
  { value: 'pio', label: 'PIO Card' },
  { value: 'student_id', label: 'Student ID' },
  { value: 'company_id', label: 'Corporate / Company ID' },
  { value: 'other', label: 'Other Document' },
];

const initialForm = {
  title: '',
  name: '', // full name (same as CheckIn) - parsed to first/middle/last on submit
  first_name: '',
  middle_name: '',
  last_name: '',
  email: '',
  phone: '',
  country_code: '+91',
  alternate_phone: '',
  alternate_phone_country_code: '+91',
  address: '', // single Guest Address field like CheckIn -> sent as address_line1
  city: '',
  state: '',
  country: '',
  pincode: '',
  dob: '',
  gender: '',
  notes: '',
  document_type: '',
  id_front: '',
  id_back: '',
  camera_upload: false,
};

const AddCustomerDrawer = ({ open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);
  const {
    selectMenuProps,
    menuProps,
    autocompleteSlotProps,
    datePickerSlotProps,
  } = useMemo(() => createBookingDrawerOverlayProps(isDark), [isDark]);
  const phoneCountryCodeAnchorRef = useRef(null);
  const titleMenuAnchorRef = useRef(null);
  const fileInputFrontRef = useRef(null);
  const fileInputBackRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mobileFieldRef = useRef(null);
  const alternatePhoneCountryCodeAnchorRef = useRef(null);

  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraType, setCameraType] = useState(null); // 'front' or 'back'
  const [stream, setStream] = useState(null);
  const [cameraPermissionSnackbar, setCameraPermissionSnackbar] = useState({ open: false, message: '' });
  const [error, setError] = useState(null);
  const [phoneCountryCodeMenuOpen, setPhoneCountryCodeMenuOpen] = useState(false);
  const [phoneCountryCodeSearch, setPhoneCountryCodeSearch] = useState('');
  const [alternatePhoneCountryCodeMenuOpen, setAlternatePhoneCountryCodeMenuOpen] = useState(false);
  const [alternatePhoneCountryCodeSearch, setAlternatePhoneCountryCodeSearch] = useState('');
  const [titleMenuOpen, setTitleMenuOpen] = useState(false);
  const [nameError, setNameError] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [alternatePhoneError, setAlternatePhoneError] = useState('');
  const [alternatePhoneTouched, setAlternatePhoneTouched] = useState(false);
  const [docImageErrors, setDocImageErrors] = useState({ id_front: '', id_back: '' });

  // Parse full name into first/middle/last - same logic as CheckIn drawer
  const parseFullName = useCallback((fullName) => {
    if (!fullName || !fullName.trim()) return { first_name: '', middle_name: '', last_name: '' };
    const normalized = fullName.trim().replace(/\s+/g, ' ');
    const parts = normalized.split(' ').filter(Boolean);
    if (parts.length === 0) return { first_name: '', middle_name: '', last_name: '' };
    if (parts.length === 1) return { first_name: parts[0], middle_name: '', last_name: '' };
    if (parts.length === 2) return { first_name: parts[0], middle_name: '', last_name: parts[1] };
    if (parts.length === 3) return { first_name: parts[0], middle_name: parts[1], last_name: parts[2] };
    const first = parts.slice(0, parts.length - 2).join(' ');
    return { first_name: first, middle_name: parts[parts.length - 2], last_name: parts[parts.length - 1] };
  }, []);

  const handleNameChange = useCallback((value) => {
    setForm((prev) => {
      const { first_name, middle_name, last_name } = parseFullName(value);
      return { ...prev, name: value, first_name, middle_name, last_name };
    });
    if (nameTouched) setNameError(!(value || '').trim() ? 'Name is required' : '');
  }, [parseFullName, nameTouched]);

  // Defined lists (same as CheckIn / walking guest form)
  const countryCodes = useMemo(() => countryCodesData, []);
  const selectedPhoneCountryCode = useMemo(() => {
    return countryCodes.find(cc => cc.dialCode === (form.country_code || '').trim()) || countryCodes.find(cc => cc.code === 'IN');
  }, [form.country_code, countryCodes]);
  const selectedAlternatePhoneCountryCode = useMemo(() => {
    return countryCodes.find(cc => cc.dialCode === (form.alternate_phone_country_code || '').trim()) || countryCodes.find(cc => cc.code === 'IN');
  }, [form.alternate_phone_country_code, countryCodes]);

  const countries = useMemo(() => countriesData.countries.map(c => ({ code: c.code, name: c.name })), []);
  const selectedCountryObj = useMemo(() => {
    if (!form.country) return null;
    return countriesData.countries.find(c => c.name === form.country || c.code === form.country);
  }, [form.country]);
  const availableStates = useMemo(() => {
    if (!selectedCountryObj) return [];
    return (selectedCountryObj.states || []).map(s => ({ code: s.code, name: s.name }));
  }, [selectedCountryObj]);
  const availableCities = useMemo(() => {
    if (!selectedCountryObj) return [];
    const stateObj = (selectedCountryObj.states || []).find(s => s.name === form.state || s.code === form.state);
    return stateObj ? (stateObj.cities || []) : [];
  }, [selectedCountryObj, form.state]);

  // Auto-select hotel configured country (and phone dial codes) when drawer opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getHotelConfiguration()
      .then((data) => {
        if (cancelled) return;
        const code = (data.country || '').trim().toUpperCase();
        if (!code) return;
        const countryObj = countriesData.countries.find((c) => c.code === code);
        const dialObj = countryCodes.find((cc) => cc.code === code);
        setForm((f) => ({
          ...f,
          country: countryObj ? countryObj.name : f.country,
          country_code: dialObj ? dialObj.dialCode : f.country_code,
          alternate_phone_country_code: dialObj ? dialObj.dialCode : f.alternate_phone_country_code,
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, countryCodes]);

  const resetForm = useCallback(() => {
    setForm(initialForm);
    setError(null);
    setNameError('');
    setNameTouched(false);
    setPhoneError('');
    setPhoneTouched(false);
    setAlternatePhoneError('');
    setAlternatePhoneTouched(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = async () => {
    const nameTrimmed = (form.name || '').trim();
    setNameTouched(true);
    if (!nameTrimmed) {
      setNameError('Name is required');
      setError('Name is required.');
      return;
    }
    setNameError('');
    setPhoneTouched(true);
    const phoneErr = validatePhone(form.phone);
    if (phoneErr) {
      setPhoneError(phoneErr);
      setError(phoneErr);
      return;
    }
    setAlternatePhoneTouched(true);
    const altErr = validateAlternatePhone(form.alternate_phone);
    if (altErr) {
      setAlternatePhoneError(altErr);
      setError(altErr);
      return;
    }
    setPhoneError('');
    setAlternatePhoneError('');
    if ((form.document_type || '').trim()) {
      const imageErrors = validateGuestDocumentImages(
        form.document_type,
        form.id_front,
        form.id_back,
      );
      setDocImageErrors(imageErrors);
      if (imageErrors.id_front || imageErrors.id_back) {
        setError(imageErrors.id_front || imageErrors.id_back);
        return;
      }
    } else {
      setDocImageErrors({ id_front: '', id_back: '' });
    }
    const phoneDigits = (form.phone || '').trim().replace(/\D/g, '');
    setSaving(true);
    setError(null);
    try {
      // Check if a customer with this mobile number already exists
      const searchResult = await searchGuestByPhone(phoneDigits);
      if (searchResult && searchResult.guest) {
        setError(null);
        setPhoneError('A customer with this mobile number already exists.');
        setPhoneTouched(true);
        setSaving(false);
        mobileFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // Country must be ISO 3166-1 alpha-2 (2-letter code), not full name
      const countryPayload = selectedCountryObj ? selectedCountryObj.code : null;
      // Pincode: backend allows A-Z0-9 spaces/hyphens, 3-10 chars
      const pincodeRaw = (form.pincode || '').trim();
      const pincodeSanitized = pincodeRaw
        ? pincodeRaw.replace(/[^A-Z0-9\s\-]/gi, '').toUpperCase().slice(0, 10)
        : '';
      const pincodePayload =
        pincodeSanitized.length >= 3 && pincodeSanitized.length <= 10 ? pincodeSanitized : null;

      const { first_name, middle_name, last_name } = parseFullName(form.name);
      const payload = {
        title: (form.title || '').trim() || null,
        first_name: (first_name || '').trim() || nameTrimmed,
        middle_name: (middle_name || '').trim() || null,
        last_name: (last_name || '').trim() || null,
        email: (form.email || '').trim() || null,
        country_code: (form.country_code || '').trim() || null,
        phone: phoneDigits || null,
        alternate_phone_country_code: (form.alternate_phone_country_code || '').trim() || null,
        alternate_phone: (form.alternate_phone || '').trim() ? normalizePhoneInputForCountry(form.alternate_phone, form.alternate_phone_country_code) : null,
        address_line1: (form.address || '').trim() || null,
        address_line2: null,
        city: (form.city || '').trim() || null,
        state: (form.state || '').trim() || null,
        country: countryPayload,
        pincode: pincodePayload,
        dob: form.dob || null,
        gender: (form.gender || '').trim() || null,
        notes: (form.notes || '').trim() || null,
        document_type: (form.document_type || '').trim() || null,
        id_front: (form.id_front || '').trim() || null,
        id_back: (form.id_back || '').trim() || null,
      };
      const result = await createCustomer(payload);
      const guestId = result?.id;
      resetForm();
      onClose();
      if (onSuccess) onSuccess(result);
      if (guestId) navigate(`/customerhub/customers/${guestId}/`);
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || 'Failed to add customer';
      const isDuplicatePhone =
        typeof errMsg === 'string' && errMsg.toLowerCase().includes('mobile number already exists');
      if (isDuplicatePhone) {
        setError(null);
        setPhoneError('A customer with this mobile number already exists.');
        setPhoneTouched(true);
        mobileFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setError(errMsg);
      }
    } finally {
      setSaving(false);
    }
  };

  // Border color for mobile/country/state/city/alternate phone - theme tokens
  const inputBorderColor = getGuestDrawerFieldBorderColor(isDark);

  // Input styling to match CheckIn drawer add guest form
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      '& fieldset': { borderColor: inputBorderColor },
      '&:hover fieldset': { borderColor: ACCENT },
      '&.Mui-focused fieldset': { borderColor: ACCENT },
    },
    '& .MuiInputBase-input': {
      backgroundColor: 'transparent !important',
      color: isDark ? drawerTheme.text : '#333',
    },
    '& .MuiInputLabel-root': { color: isDark ? drawerTheme.subtleText : '#666' },
  };

  const walkinFieldSx = (error) => ({
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      '& fieldset': { borderColor: error ? theme.palette.error.main : inputBorderColor },
      '&:hover fieldset': { borderColor: error ? theme.palette.error.main : ACCENT },
      '&.Mui-focused fieldset': { borderColor: error ? theme.palette.error.main : ACCENT },
    },
    '& .MuiInputBase-input': {
      backgroundColor: 'transparent !important',
      color: isDark ? drawerTheme.text : '#333',
    },
    '& .MuiInputLabel-root': { color: isDark ? drawerTheme.subtleText : '#666' },
  });

  const validatePhone = useCallback((phone, dialCode = form.country_code) => (
    validatePhoneForCountry(phone, dialCode)
  ), [form.country_code]);
  const validateAlternatePhone = useCallback((phone, dialCode = form.alternate_phone_country_code) => (
    validatePhoneForCountry(phone, dialCode, { required: false, label: 'Alternate mobile' })
  ), [form.alternate_phone_country_code]);

  // Select/dropdown styling - same as CheckIn (notchedOutline)
  const selectSx = {
    '& .MuiOutlinedInput-notchedOutline': { borderColor: inputBorderColor },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCENT },
  };

  // Select styling for Gender - same as WalkIn guest details (inputBorderColor)
  const selectWalkinSx = {
    '& .MuiOutlinedInput-notchedOutline': { borderColor: inputBorderColor },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: inputBorderColor },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: inputBorderColor },
  };

  const getDocumentTypeLabel = () => {
    if (!form.document_type) return 'Document';
    const opt = documentUploadOptions.find((o) => o.value === form.document_type);
    return opt ? opt.label : 'Document';
  };
  const getFrontDocumentLabel = () => `Upload ${getDocumentTypeLabel()} Front`;
  const getBackDocumentLabel = () => `Upload ${getDocumentTypeLabel()} Back`;

  const clearDocImageError = () => {
    setDocImageErrors((prev) =>
      prev.id_front || prev.id_back ? { id_front: '', id_back: '' } : prev,
    );
  };

  const syncDocImageErrors = (idFront, idBack, documentType = form.document_type) => {
    setDocImageErrors(validateGuestDocumentImages(documentType, idFront, idBack));
  };

  const handleFileUpload = (e, type) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        clearDocImageError();
        setForm((prev) => ({ ...prev, [type === 'front' ? 'id_front' : 'id_back']: reader.result }));
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const showCameraPermissionMessage = (message) => {
    setCameraPermissionSnackbar({
      open: true,
      message:
        message ||
        'Camera access was denied or is unavailable. Allow camera permission in your browser settings for this site, or turn off Camera upload to choose a file.',
    });
  };

  const startCamera = async (type) => {
    try {
      const nativeImageData = await captureNativeCameraDataUrl();
      if (nativeImageData) {
        clearDocImageError();
        setForm((prev) => ({ ...prev, [type === 'front' ? 'id_front' : 'id_back']: nativeImageData }));
        return;
      }
    } catch (error) {
      const message = getNativeCameraErrorMessage(error);
      if (message) showCameraPermissionMessage(message);
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      showCameraPermissionMessage(
        'Camera is not available in this browser or page context. Turn off Camera upload to upload a file instead.'
      );
      return;
    }
    try {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      setStream(mediaStream);
      setCameraType(type);
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (error) {
      showCameraPermissionMessage();
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setCameraOpen(false);
    setCameraType(null);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      clearDocImageError();
      setForm((prev) => ({
        ...prev,
        [cameraType === 'front' ? 'id_front' : 'id_back']: imageData,
      }));
      stopCamera();
    }
  };

  const handleUploadClick = (type) => {
    if (form.camera_upload) {
      startCamera(type);
    } else {
      if (type === 'front') {
        fileInputFrontRef.current?.click();
      } else {
        fileInputBackRef.current?.click();
      }
    }
  };

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={getDrawerRootSx()}
      PaperProps={{
        sx: getBookingDrawerPaperSx(isDark, {
          width: { xs: '100%', sm: 600 },
        }),
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header - same as CheckIn drawer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            ...getBookingDrawerHeaderSx(isDark),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon sx={{ color: ACCENT, fontSize: 24 }} />
            <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600 }}>
              Add Customer
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content - scrollable form (same layout as CheckIn drawer add guest) */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 1.5,
            ...getBookingDrawerTextFieldSx(isDark),
            '& .MuiOutlinedInput-root.Mui-focused fieldset': {
              borderColor: ACCENT,
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: ACCENT,
            },
          }}
        >
          {error && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>{error}</Typography>
          )}
          <Grid container spacing={2}>
            {/* First Row: Mobile Number, Name with Title - same as CheckIn */}
            <Grid item xs={12} md={6} ref={mobileFieldRef}>
              <Box ref={phoneCountryCodeAnchorRef}>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  required
                  value={form.phone ?? ''}
                  onChange={(e) => {
                    const digits = normalizePhoneInputForCountry(e.target.value, form.country_code);
                    setForm((f) => ({ ...f, phone: digits }));
                    if (phoneTouched) setPhoneError(validatePhone(digits));
                  }}
                  onBlur={() => {
                    setPhoneTouched(true);
                    setPhoneError(validatePhone(form.phone));
                  }}
                  error={!!phoneError && phoneTouched}
                  helperText={phoneTouched ? phoneError : `Enter ${formatPhoneLengthLabel(form.country_code)}`}
                  inputProps={{
                    maxLength: normalizePhoneInputForCountry('9'.repeat(20), form.country_code).length,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          onClick={() => setPhoneCountryCodeMenuOpen(true)}
                          sx={{
                            display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 0.5, px: 1, py: 0.5, borderRadius: 1,
                            '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' },
                          }}
                        >
                          <Typography component="span" sx={{ fontSize: '1.2rem' }}>{selectedPhoneCountryCode?.flag || '🇮🇳'}</Typography>
                          <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{selectedPhoneCountryCode?.dialCode || '+91'}</Typography>
                          <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>▼</Typography>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={walkinFieldSx(!!phoneError && phoneTouched)}
                />
              </Box>
              <Menu
                {...menuProps}
                anchorEl={phoneCountryCodeAnchorRef.current}
                open={phoneCountryCodeMenuOpen}
                onClose={() => { setPhoneCountryCodeMenuOpen(false); setPhoneCountryCodeSearch(''); }}
                PaperProps={{
                  ...menuProps.PaperProps,
                  sx: { ...menuProps.PaperProps.sx, maxHeight: 400, width: 300 },
                }}
              >
                <Box sx={{ p: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search country..."
                    value={phoneCountryCodeSearch}
                    onChange={(e) => setPhoneCountryCodeSearch(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                </Box>
                {countryCodes
                  .filter((c) => {
                    if (!phoneCountryCodeSearch) return true;
                    const q = phoneCountryCodeSearch.toLowerCase();
                    return c.name.toLowerCase().includes(q) || c.dialCode.includes(q) || (c.code || '').toLowerCase().includes(q);
                  })
                  .map((c) => (
                    <MenuItem
                      key={c.code}
                      onClick={() => {
                        const normalizedPhone = normalizePhoneInputForCountry(form.phone, c.dialCode);
                        setForm((f) => ({ ...f, country_code: c.dialCode, phone: normalizedPhone }));
                        if (phoneTouched) setPhoneError(validatePhone(normalizedPhone, c.dialCode));
                        setPhoneCountryCodeMenuOpen(false);
                        setPhoneCountryCodeSearch('');
                      }}
                      selected={selectedPhoneCountryCode?.dialCode === c.dialCode}
                    >
                      <ListItemIcon><Typography sx={{ fontSize: '1.2rem' }}>{c.flag}</Typography></ListItemIcon>
                      <ListItemText primary={c.dialCode} secondary={c.name} primaryTypographyProps={{ fontWeight: 500 }} />
                    </MenuItem>
                  ))}
              </Menu>
            </Grid>
            {/* Name with Title - same as CheckIn add guest */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={form.name || ''}
                onChange={(e) => handleNameChange(e.target.value)}
                onBlur={() => {
                  setNameTouched(true);
                  if (!(form.name || '').trim()) setNameError('Name is required');
                }}
                required
                error={!!nameError && nameTouched}
                helperText={nameTouched ? nameError : ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        ref={titleMenuAnchorRef}
                        onClick={() => setTitleMenuOpen(true)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          gap: 0.5,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                          },
                        }}
                      >
                        <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.875rem', minWidth: 40 }}>
                          {form.title || 'Title'}
                        </Typography>
                        <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          ▼
                        </Typography>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'transparent !important',
                    '& fieldset': {
                      borderColor: (nameError && nameTouched) ? theme.palette.error.main : (isDark ? '#444' : '#e0e0e0'),
                    },
                    '&:hover fieldset': {
                      borderColor: (nameError && nameTouched) ? theme.palette.error.main : ACCENT,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: (nameError && nameTouched) ? theme.palette.error.main : ACCENT,
                    },
                  },
                  '& .MuiInputBase-input': {
                    backgroundColor: 'transparent !important',
                    color: isDark ? drawerTheme.text : '#333',
                  },
                  '& .MuiInputLabel-root': { color: isDark ? drawerTheme.subtleText : '#666' },
                }}
              />
              <Menu
                {...menuProps}
                anchorEl={titleMenuAnchorRef.current}
                open={titleMenuOpen}
                onClose={() => setTitleMenuOpen(false)}
                PaperProps={{
                  ...menuProps.PaperProps,
                  sx: { ...menuProps.PaperProps.sx, maxHeight: 300, minWidth: 150 },
                }}
              >
                {titleOptions.map((opt) => (
                  <MenuItem
                    key={opt.value || 'none'}
                    onClick={() => {
                      setForm((f) => ({ ...f, title: opt.value }));
                      setTitleMenuOpen(false);
                    }}
                    selected={form.title === opt.value}
                  >
                    {opt.value ? opt.label : <em>None</em>}
                  </MenuItem>
                ))}
              </Menu>
            </Grid>

            {/* Row: Email, Gender */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={form.email || ''}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                inputProps={{ autoComplete: 'email' }}
                sx={walkinFieldSx(false)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={selectWalkinSx}>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={form.gender || ''}
                  label="Gender"
                  MenuProps={selectMenuProps}
                  onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}
                  sx={selectWalkinSx}
                >
                  {GENDERS.map((g) => (
                    <MenuItem key={g.value || 'blank'} value={g.value}>{g.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Guest Address - same as CheckIn */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Guest Address"
                value={form.address || ''}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                multiline
                rows={2}
                sx={inputSx}
              />
            </Grid>

            {/* Country, State, City - same design and layout as WalkIn guest details (md={3}) */}
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={countries}
                slotProps={autocompleteSlotProps}
                getOptionLabel={(opt) => opt.name}
                filterOptions={countryFilterOptions}
                value={countries.find((c) => c.name === form.country) || null}
                onChange={(e, newVal) => {
                  setForm((f) => ({
                    ...f,
                    country: newVal ? newVal.name : '',
                    state: '',
                    city: '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country"
                    sx={walkinFieldSx(false)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={availableStates}
                slotProps={autocompleteSlotProps}
                getOptionLabel={(opt) => opt.name}
                filterOptions={stateFilterOptions}
                value={availableStates.find((s) => s.name === form.state) || null}
                onChange={(e, newVal) => {
                  setForm((f) => ({
                    ...f,
                    state: newVal ? newVal.name : '',
                    city: '',
                  }));
                }}
                disabled={!selectedCountryObj}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    helperText={selectedCountryObj ? 'Select a state' : 'Select country first'}
                    sx={walkinFieldSx(false)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={availableCities}
                slotProps={autocompleteSlotProps}
                getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt)}
                filterOptions={cityFilterOptions}
                value={form.city || null}
                onChange={(e, newVal) => setForm((f) => ({ ...f, city: newVal || '' }))}
                freeSolo
                disabled={!selectedCountryObj || !form.state}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="City"
                    helperText={selectedCountryObj && form.state ? 'Select a city' : form.state ? 'Select state first' : 'Select country first'}
                    sx={walkinFieldSx(false)}
                  />
                )}
              />
            </Grid>
            {/* Pincode - same row as Country/State/City (WalkIn layout), same design */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Pincode"
                value={form.pincode || ''}
                onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value.slice(0, 10) }))}
                inputProps={{ maxLength: 10 }}
                sx={walkinFieldSx(false)}
              />
            </Grid>

            {/* Row: Date of Birth, Alternate mobile - after country row; DOB same design as guest details form */}
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Date of Birth"
                  value={form.dob ? moment(form.dob) : null}
                  onChange={(date) => {
                    if (date && date.isValid()) {
                      if (date.isAfter(moment())) {
                        return;
                      }
                      setForm((f) => ({ ...f, dob: date.format('YYYY-MM-DD') }));
                    } else {
                      setForm((f) => ({ ...f, dob: '' }));
                    }
                  }}
                  maxDate={moment()}
                  slotProps={{
                    ...datePickerSlotProps,
                    textField: {
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'transparent !important',
                          '& fieldset': {
                            borderColor: inputBorderColor,
                          },
                          '&:hover fieldset': {
                            borderColor: inputBorderColor,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: inputBorderColor,
                          },
                        },
                        '& .MuiInputBase-input': {
                          backgroundColor: 'transparent !important',
                          color: isDark ? drawerTheme.text : '#333',
                        },
                        '& .MuiInputLabel-root': { color: isDark ? drawerTheme.subtleText : '#666' },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box ref={alternatePhoneCountryCodeAnchorRef}>
                <TextField
                  fullWidth
                  label="Alternate mobile"
                  value={form.alternate_phone ?? ''}
                  onChange={(e) => {
                    const digits = normalizePhoneInputForCountry(e.target.value, form.alternate_phone_country_code);
                    setForm((f) => ({ ...f, alternate_phone: digits }));
                    if (alternatePhoneTouched) setAlternatePhoneError(validateAlternatePhone(digits));
                  }}
                  onBlur={() => {
                    setAlternatePhoneTouched(true);
                    setAlternatePhoneError(validateAlternatePhone(form.alternate_phone));
                  }}
                  error={!!alternatePhoneError && alternatePhoneTouched}
                  helperText={alternatePhoneTouched ? alternatePhoneError : `Optional · ${formatPhoneLengthLabel(form.alternate_phone_country_code)}`}
                  inputProps={{
                    maxLength: normalizePhoneInputForCountry('9'.repeat(20), form.alternate_phone_country_code).length,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          onClick={() => setAlternatePhoneCountryCodeMenuOpen(true)}
                          sx={{
                            display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 0.5, px: 1, py: 0.5, borderRadius: 1,
                            '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' },
                          }}
                        >
                          <Typography component="span" sx={{ fontSize: '1.2rem' }}>{selectedAlternatePhoneCountryCode?.flag || '🇮🇳'}</Typography>
                          <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{selectedAlternatePhoneCountryCode?.dialCode || '+91'}</Typography>
                          <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>▼</Typography>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={walkinFieldSx(!!alternatePhoneError && alternatePhoneTouched)}
                />
              </Box>
              <Menu
                {...menuProps}
                anchorEl={alternatePhoneCountryCodeAnchorRef.current}
                open={alternatePhoneCountryCodeMenuOpen}
                onClose={() => { setAlternatePhoneCountryCodeMenuOpen(false); setAlternatePhoneCountryCodeSearch(''); }}
                PaperProps={{
                  ...menuProps.PaperProps,
                  sx: { ...menuProps.PaperProps.sx, maxHeight: 400, width: 300 },
                }}
              >
                <Box sx={{ p: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search country..."
                    value={alternatePhoneCountryCodeSearch}
                    onChange={(e) => setAlternatePhoneCountryCodeSearch(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                </Box>
                {countryCodes
                  .filter((c) => {
                    if (!alternatePhoneCountryCodeSearch) return true;
                    const q = alternatePhoneCountryCodeSearch.toLowerCase();
                    return c.name.toLowerCase().includes(q) || c.dialCode.includes(q) || (c.code || '').toLowerCase().includes(q);
                  })
                  .map((c) => (
                    <MenuItem
                      key={c.code}
                      onClick={() => {
                        const normalizedPhone = normalizePhoneInputForCountry(form.alternate_phone, c.dialCode);
                        setForm((f) => ({ ...f, alternate_phone_country_code: c.dialCode, alternate_phone: normalizedPhone }));
                        if (alternatePhoneTouched) setAlternatePhoneError(validateAlternatePhone(normalizedPhone, c.dialCode));
                        setAlternatePhoneCountryCodeMenuOpen(false);
                        setAlternatePhoneCountryCodeSearch('');
                      }}
                      selected={selectedAlternatePhoneCountryCode?.dialCode === c.dialCode}
                    >
                      <ListItemIcon><Typography sx={{ fontSize: '1.2rem' }}>{c.flag}</Typography></ListItemIcon>
                      <ListItemText primary={c.dialCode} secondary={c.name} primaryTypographyProps={{ fontWeight: 500 }} />
                    </MenuItem>
                  ))}
              </Menu>
            </Grid>

            {/* Notes - before Documents */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={form.notes || ''}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                sx={walkinFieldSx(false)}
              />
            </Grid>

            {/* Document Upload Section - same design as CheckIn add guest */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Select Document type</InputLabel>
                    <Select
                      value={form.document_type || ''}
                      label="Select Document type"
                      MenuProps={selectMenuProps}
                      onChange={(e) => {
                        const next = e.target.value;
                        setForm((f) => ({ ...f, document_type: next }));
                        if (!String(next || '').trim()) {
                          setDocImageErrors({ id_front: '', id_back: '' });
                        }
                      }}
                      sx={selectSx}
                    >
                      <MenuItem value="">
                        <em>Select document type</em>
                      </MenuItem>
                      {documentUploadOptions.map((doc) => (
                        <MenuItem key={doc.value} value={doc.value}>
                          {doc.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
                    <FormControlLabel
                      label="Camera upload"
                      labelPlacement="start"
                      control={
                        <Switch
                          checked={form.camera_upload}
                          onChange={(e) => setForm((f) => ({ ...f, camera_upload: e.target.checked }))}
                          color="primary"
                        />
                      }
                      sx={{ color: isDark ? drawerTheme.text : '#333' }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            {/* ID Front */}
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: isDark ? drawerTheme.text : '#333' }}>
                {getFrontDocumentLabel()}
                {(form.document_type || '').trim() && !form.id_front && !form.id_back ? ' *' : ''}
              </Typography>
              <Paper
                sx={getGuestDrawerUploadPaperSx(isDark, {
                  ...(docImageErrors.id_front
                    ? {
                        borderColor: '#d32f2f',
                        backgroundColor: isDark ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.04)',
                        '&:hover': {
                          borderColor: '#d32f2f',
                          backgroundColor: isDark ? 'rgba(244, 67, 54, 0.12)' : 'rgba(244, 67, 54, 0.06)',
                        },
                      }
                    : {}),
                })}
                onClick={() => handleUploadClick('front')}
              >
                {form.id_front ? (
                  <Box>
                    <img
                      src={form.id_front}
                      alt="ID Front"
                      style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 4 }}
                    />
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        const nextFront = '';
                        const nextBack = form.id_back;
                        setForm((f) => ({ ...f, id_front: nextFront }));
                        syncDocImageErrors(nextFront, nextBack);
                      }}
                      sx={{ mt: 1 }}
                    >
                      Remove
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    {form.camera_upload ? (
                      <CameraAltIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                    ) : (
                      <CloudUploadIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                    )}
                    <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                      {form.camera_upload ? 'Click to capture from camera' : 'Click to upload or drag and drop'}
                    </Typography>
                  </Box>
                )}
                <input
                  ref={fileInputFrontRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileUpload(e, 'front')}
                />
              </Paper>
              {docImageErrors.id_front && (
                <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                  {docImageErrors.id_front}
                </Typography>
              )}
            </Grid>
            {/* ID Back */}
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: isDark ? drawerTheme.text : '#333' }}>
                {getBackDocumentLabel()}
                {(form.document_type || '').trim() && !form.id_front && !form.id_back ? ' *' : ''}
              </Typography>
              <Paper
                sx={getGuestDrawerUploadPaperSx(isDark, {
                  ...(docImageErrors.id_back
                    ? {
                        borderColor: '#d32f2f',
                        backgroundColor: isDark ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.04)',
                        '&:hover': {
                          borderColor: '#d32f2f',
                          backgroundColor: isDark ? 'rgba(244, 67, 54, 0.12)' : 'rgba(244, 67, 54, 0.06)',
                        },
                      }
                    : {}),
                })}
                onClick={() => handleUploadClick('back')}
              >
                {form.id_back ? (
                  <Box>
                    <img
                      src={form.id_back}
                      alt="ID Back"
                      style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 4 }}
                    />
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        const nextFront = form.id_front;
                        const nextBack = '';
                        setForm((f) => ({ ...f, id_back: nextBack }));
                        syncDocImageErrors(nextFront, nextBack);
                      }}
                      sx={{ mt: 1 }}
                    >
                      Remove
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    {form.camera_upload ? (
                      <CameraAltIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                    ) : (
                      <CloudUploadIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                    )}
                    <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                      {form.camera_upload ? 'Click to capture from camera' : 'Click to upload or drag and drop'}
                    </Typography>
                  </Box>
                )}
                <input
                  ref={fileInputBackRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileUpload(e, 'back')}
                />
              </Paper>
              {docImageErrors.id_back && (
                <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                  {docImageErrors.id_back}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Footer - full width: half Cancel, half Add Customer */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            width: '100%',
            gap: 1,
            ...getBookingDrawerFooterSx(isDark),
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={saving}
            sx={{ flex: 1, textTransform: 'none', ...getBookingDrawerOutlinedButtonSx(isDark) }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <PersonAddIcon />}
            sx={{ flex: 1, textTransform: 'none', ...getBookingDrawerPrimaryButtonSx(isDark) }}
          >
            {saving ? 'Adding…' : 'Add Customer'}
          </Button>
        </Box>
      </Box>

      {/* Camera Modal - same as guest details form */}
      {cameraOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              stopCamera();
            }
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '800px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
              {cameraType === 'front' ? 'Capture ID Front' : 'Capture ID Back'}
            </Typography>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '600px',
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: 'black',
                border: '2px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3, width: '100%' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={capturePhoto}
                size="large"
                sx={{ minWidth: 150, py: 1.5, fontSize: '1rem', fontWeight: 600 }}
              >
                Capture Photo
              </Button>
              <Button
                variant="outlined"
                onClick={stopCamera}
                size="large"
                sx={{
                  minWidth: 150,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Drawer>
    <Snackbar
      open={cameraPermissionSnackbar.open}
      autoHideDuration={9000}
      onClose={() => setCameraPermissionSnackbar((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setCameraPermissionSnackbar((s) => ({ ...s, open: false }))}
        severity="warning"
        variant="filled"
        sx={{ width: '100%', alignItems: 'center' }}
      >
        {cameraPermissionSnackbar.message}
      </Alert>
    </Snackbar>
    </>
  );
};

export default AddCustomerDrawer;
